import classNames from "classnames";
import { State } from "redux/store";
import { motion } from "framer-motion";
import { connect } from "react-redux";
import { useEffect, useRef, useState } from "react";
import { BiInfoCircle } from "react-icons/bi";
import AutoSizer from "react-virtualized-auto-sizer";
import { VariableSizeList as List } from "react-window";
import TooltipWrapper from "views/uikit/TooltipWrapper";
import { formatNumberWithCommas } from "helpers/utility/formatters";
import useAutosizerScrollStatus from "hooks/useAutosizerScrollStatus";

import {
  getDuplicateThresholdRange,
  getIsDuplicatesThresholdEnabled,
} from "redux/SingleDataset/selectors";
import {
  getShowDuplicatesRange,
  getShowMislabelsRange,
} from "redux/Metadata/selectors";
import { useAccordionContext } from "contexts/AccordionContext";

import { FilterItemCountsWithSelect, MetadataBarTypes } from "types";

import SunIcon from "assets/icons/SunIcon";
import MoonIcon from "assets/icons/MoonIcon";
import ImageIcon from "assets/icons/imageIcon";
import OutliersIcon from "assets/icons/OutliersIcon";
import DuplicateIcon from "assets/icons/DuplicateIcon";
import MislabelsIcon from "assets/icons/MislabelsIcon";
import LabelOutliersIcon from "assets/icons/LabelOutliersIcon";

import "rc-slider/assets/index.css";
import styles from "./style.module.scss";
import AnimatedBar from "./AnimatedSingleBar";
import ContentBodyLoader from "./ContentBodyLoader";

const MARGIN_TOP = 16;
const HEADER_HEIGHT = 32;
const HEADER_PADDING = 16;
const RIGHT_PANE_INFO_TOOLTIP_ID = "right-pane-info-tooltip";

const issuesIconMap: Record<string, JSX.Element> = {
  "class outliers": <LabelOutliersIcon />,
  duplicates: <DuplicateIcon />,
  mislabels: <MislabelsIcon />,
  outliers: <OutliersIcon />,
  bright: <SunIcon />,
  blurry: <ImageIcon />,
  dark: <MoonIcon />,
};

const issuesInfoMap: Record<string, string> = {
  mislabels:
    "Finds images that were likely given the wrong label. Based on how they look, they fit better in another group, even if label names aren’t meaningful.",
  duplicates:
    "Detects groups of identical or visually similar images. Adjust sensitivity to control how close the matches are.",
  outliers:
    "Flags images that look very different from the rest of the dataset and don’t fit the typical pattern.",
  blurry:
    "Flags images that appear out of focus or lack sharp detail compared to the rest of the dataset.",
  dark: "Detects images that are unusually dark or underexposed relative to the overall dataset.",
  bright:
    "Detects images that are unusually bright or overexposed relative to the overall dataset.",
  "class outliers":
    "Identifies mislabeled examples with no correct class in the vocabulary, misplaced bounding boxes or examples that are out-of-distribution for the class.",
};

const NoMatchingData = () => (
  <div className={styles.noMatchcontainer}>No Matching Data.</div>
);

interface RowProps {
  index: number;
  info?: string;
  maxCount: number;
  showCount: boolean;
  isLoading: boolean;
  barIcon?: JSX.Element;
  style: React.CSSProperties;
  data: Array<FilterItemCountsWithSelect>;
}

const Row = ({
  data,
  info,
  index,
  style,
  barIcon,
  maxCount,
  showCount,
  isLoading,
}: RowProps) => {
  const singleLabel = data[index];
  const paddingTop = index === 0 || index === data.length - 1 ? "1px" : "0";
  const paddingBottom = index === data.length - 1 ? 0 : "8px";
  const percentage = singleLabel.ratio
    ? Math.round(singleLabel.ratio * 100)
    : 0;

  return (
    <div
      key={index}
      className={styles.singleBarRow}
      style={{ ...style, padding: `${paddingTop} 16px ${paddingBottom} 16px` }}
    >
      <SingleBarDisplay
        key={index}
        info={info}
        index={index}
        barIcon={barIcon}
        maxCount={maxCount}
        isLoading={isLoading}
        showCount={showCount}
        percentage={percentage}
        count={singleLabel.count}
        title={singleLabel.title}
      />
    </div>
  );
};

interface SingleBarDisplayProps {
  title: string;
  info?: string;
  index: number;
  count: number;
  maxCount: number;
  isLoading: boolean;
  showCount: boolean;
  percentage: number;
  barIcon?: JSX.Element;
}

const SingleBarDisplay = ({
  info,
  index,
  title,
  count,
  barIcon,
  maxCount,
  showCount,
  isLoading,
  percentage,
}: SingleBarDisplayProps) => {
  const countWidth = `${maxCount.toString().length + 1}ch`;

  const barTitleClassnames = classNames(styles.barTitle, {
    [styles.titleWithoutIcon]: !barIcon,
  });

  return (
    <AnimatedBar
      className={styles.singleBarDisplay}
      loading={isLoading}
      key={index}
    >
      <div className={styles.barContainer}>
        <div className={styles.leftSection}>
          {barIcon && <div className={styles.barIcon}>{barIcon}</div>}
          <span className={barTitleClassnames} title={title}>
            {title}
          </span>
          {info && (
            <span
              className={styles.info}
              data-tooltip-id={RIGHT_PANE_INFO_TOOLTIP_ID}
              data-tooltip-position-strategy="fixed"
              data-tooltip-content={info}
              data-tooltip-place="top"
            >
              <BiInfoCircle />
            </span>
          )}
        </div>

        <div className={styles.rightSection}>
          {isLoading ? (
            <ContentBodyLoader />
          ) : showCount ? (
            <div className={styles.barCount} style={{ maxWidth: countWidth }}>
              {formatNumberWithCommas(count)}
            </div>
          ) : (
            <div className={styles.barPercentage}>{percentage}%</div>
          )}
        </div>
      </div>

      <div className={styles.percentageBarContainer}>
        <motion.div
          className={styles.percentageBar}
          transition={{ duration: 0.5 }}
          animate={{ width: `${percentage}%` }}
        ></motion.div>
      </div>
    </AnimatedBar>
  );
};

interface BarDisplayContentProps {
  showCount: boolean;
  isLoading: boolean;
  searchDisabled: boolean;
  isContentSelected: boolean;
  showNoMatchingData: boolean;
  type: MetadataBarTypes;
  data: Array<FilterItemCountsWithSelect>;
}

const BarDisplayContent = ({
  type,
  data,
  showCount,
  isLoading,
  searchDisabled,
  isContentSelected,
  showNoMatchingData,
}: BarDisplayContentProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<any>(null);

  const maxCount = Math.max(...data.map((item: any) => item.count));
  const { setHeightLimits, height: contextHeight } = useAccordionContext();
  const [calculatedHeight, setCalculatedHeight] = useState(contextHeight);
  const { isOverflowing, isAtBottom } = useAutosizerScrollStatus(
    containerRef,
    listRef,
    {
      shouldObserveInnerRef: type !== "issue-types",
      dependencyList: [data.length, isContentSelected],
    }
  );

  const extraHeight = searchDisabled
    ? MARGIN_TOP
    : HEADER_HEIGHT + HEADER_PADDING + MARGIN_TOP;

  const itemSize = (index?: number) => {
    if (index === data.length - 1) return 52; // Set a smaller height for the last item
    return 62; // Set a default item height
  };

  // Calculate heights for the accordion
  const calculateHeight = (numBars: number) => {
    const singleBarHeight = 62; // Default item height
    const lastBarHeight = data.length > 1 ? 52 : 62; // Last item height
    const totalHeight = (numBars - 1) * singleBarHeight + lastBarHeight;

    return totalHeight + extraHeight;
  };

  const dynamicWrapperHeight = () => {
    if (isContentSelected) {
      return { height: "100%", maxHeight: "100%" };
    } else if (calculatedHeight) {
      return { height: `${calculatedHeight - extraHeight}px` };
    }

    const fallback = calculateHeight(Math.min(data.length, 5));
    return { height: `${fallback - extraHeight}px` };
  };

  useEffect(() => setCalculatedHeight(contextHeight), [contextHeight]);

  useEffect(() => {
    const initialHeight = calculateHeight(Math.min(data.length, 5));
    const minHeight = calculateHeight(Math.min(data.length, 2));
    const maxHeight = calculateHeight(data.length);

    if (
      calculatedHeight &&
      calculatedHeight >= minHeight &&
      calculatedHeight <= maxHeight
    ) {
      return;
    }
    setHeightLimits({ initialHeight, minHeight, maxHeight });
    //eslint-disable-next-line
  }, [data.length, extraHeight]);

  return showNoMatchingData ? (
    <NoMatchingData />
  ) : (
    <div
      ref={containerRef}
      className={classNames(styles.wrapper, styles.barListWrapper, {
        [styles.overflowingWrapper]: isOverflowing && !isAtBottom,
      })}
      style={dynamicWrapperHeight()}
    >
      <TooltipWrapper id={RIGHT_PANE_INFO_TOOLTIP_ID} maxWidth="320px" />
      <AutoSizer>
        {({ height, width }: any) => (
          <List
            innerRef={listRef}
            outerRef={containerRef}
            height={height}
            width={width}
            itemCount={data.length}
            itemSize={itemSize}
            itemData={data}
            className={`List ${styles.barListContainer}`}
            key={JSON.stringify(type + showCount)}
          >
            {({ index, style }) => {
              const barIcon =
                (type === "issue-types" && issuesIconMap[data[index].title]) ||
                undefined;
              const info =
                (type === "issue-types" && issuesInfoMap[data[index].title]) ||
                undefined;
              return (
                <Row
                  data={data}
                  info={info}
                  index={index}
                  style={style}
                  barIcon={barIcon}
                  maxCount={maxCount}
                  isLoading={isLoading}
                  showCount={showCount}
                />
              );
            }}
          </List>
        )}
      </AutoSizer>
    </div>
  );
};

const mapStatesToProps = (state: State) => {
  return {
    isDuplicateThresholdEnabled: getIsDuplicatesThresholdEnabled(state),
    duplicateThresholdRange: getDuplicateThresholdRange(state),
    showDuplicatesRange: getShowDuplicatesRange(state),
    showMislabelsRange: getShowMislabelsRange(state),
  };
};

export default connect(mapStatesToProps)(BarDisplayContent);
